<template>
    <card title="Label Templates">
        <b-table :items="templates" :fields="tableFields">
            <template #cell(active)="{ value }">
                {{ value ? "✅" : "❌" }}
            </template>
            <template #cell(type)="{ value }">
                {{ LabelTemplateType[value] }}
            </template>
            <template #cell(stores)="{ value }">
                <b-badge v-if="!value || !value.length" variant="primary">Any Store</b-badge>
                <template v-else>
                    <b-badge v-for="v in value" variant="info" :key="v.id">{{ v.name }}</b-badge>
                </template>
            </template>
            <template #cell(programs)="{ value }">
                <b-badge v-if="!value || !value.length" variant="primary">Any Program</b-badge>
                <template v-else>
                    <b-badge v-for="v in value" variant="info" :key="v.id">{{ v.name }}</b-badge>
                </template>
            </template>
            <template #cell(states)="{ value }">
                <b-badge v-if="!value || !value.length" variant="primary">Any State</b-badge>
                <template v-else>
                    <b-badge v-for="v in value" variant="info" :key="v">{{ v }}</b-badge>
                </template>
            </template>
            <template #cell(actions)="data">
                <div class="table-icon-actions">
                    <a href="#" size="sm" variant="primary" @click.prevent="loadTemplate(data.item)">
                        <b-icon icon="pencil-square"></b-icon>
                    </a>
                </div>
            </template>
        </b-table>

        <BottomPagerBar hide-pager>
            <template #left>
                <b-button size="sm" variant="primary" @click="newTemplate">
                    New Template
                </b-button>
                <b-button size="sm" variant="info" @click="showTemplateTester = true">
                    Template Tester
                </b-button>
            </template>
        </BottomPagerBar>

        <b-modal :visible="!!activeTemplate" title="Template" @hidden="activeTemplate = null" no-fade size="xl"
            ok-title="Save Template" @ok="saveTemplate">
            <b-row v-if="activeTemplate">

                <b-col>
                    <b-form-group>
                        <div class="d-flex align-items-center">
                            <label class="mb-0 mr-1">Active</label>
                            <b-checkbox switch v-model="activeTemplate.active" />
                        </div>
                    </b-form-group>
                    <b-form-group label="Name">
                        <b-input v-model="activeTemplate.name" />
                    </b-form-group>
                    <b-form-group>
                        <EnumSelector v-model="activeTemplate.type" :enum-codes="LabelTemplateType" label="Type"
                            required />
                    </b-form-group>
                    <b-form-group label="Limit to Stores">
                        <multiselect v-model="activeTemplate.stores" :options="stores" :close-on-select="false"
                            :clear-on-select="false" label="name" track-by="id" multiple />
                    </b-form-group>
                    <b-form-group label="Limit to Programs">
                        <multiselect v-model="activeTemplate.programs" :options="programs" :close-on-select="false"
                            :clear-on-select="false" label="name" track-by="id" multiple />
                    </b-form-group>
                    <b-form-group label="Limit to States">
                        <multiselect v-model="activeTemplate.states" :options="states" :close-on-select="false"
                            :clear-on-select="false" multiple />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-popover target="template-zpl" triggers="focus" placement="left"
                        custom-class="text-message-template">
                        <div v-if="availableTokens.length">
                            <h6>Available Tokens <small class="text-muted">Click to add</small></h6>
                            <div class="tokens">
                                <code v-for="tkn in availableTokens" :key="tkn" @click="addToken(tkn)">
                            {{ "\{\{" + tkn + "\}\}" }}
                        </code>
                            </div>
                        </div>
                        <h6 class="m-0 p-0">Limit Token Value Length</h6>
                        To limit the maximium length of a token replacement value, include a colon plus the maximium
                        length:

                        <code>{{ "\{\{GenericForLong:20\}\}" }}</code>
                    </b-popover>
                    <b-form-group label="Template ZPL">
                        <b-textarea ref="templateZpl" id="template-zpl" v-model="activeTemplate.template" />
                    </b-form-group>
                    <b-button size="sm" @click="getDefaultTemplate" block>
                        Load System Default Template
                    </b-button>
                </b-col>
            </b-row>

            <template #modal-footer="{ ok, close }">
                <b-button @click="close">Cancel</b-button>
                <div>
                    <!-- <b-button @click="ok" variant="primary">Save and Preview</b-button> -->
                    <b-button @click="ok" variant="success">Save Template</b-button>
                </div>
            </template>
        </b-modal>

        <b-modal v-model="showTemplateTester" title="Template Tester" size="xl" hide-footer no-fade>
            <b-row>
                <b-col cols="12" xl="4">
                    <b-form-group>
                        <EnumSelector v-model="previewTemplate.type" :enum-codes="LabelTemplateType" label="Type"
                            required />
                    </b-form-group>

                    <SearchComponent v-model="previewTemplate.store" label="Store" :search-u-r-l="`/Store`"
                        :prepopulate="true" :clearable="false" />

                    <SearchComponent v-model="previewTemplate.program" label="Program" :search-u-r-l="`/Program`"
                        :prepopulate="true" :clearable="false" />

                    <b-form-group label="State">
                        <b-select v-model="previewTemplate.state" :options="states" />
                    </b-form-group>

                    <hr>

                    <SearchComponent v-model="previewTemplate.drug" :label="`Drug`"
                        :params="{ onlyActive: false, allSources: false }" :search-u-r-l="`/Drug`" />

                    <!-- <b-form-group label="Language Code">
                        <b-input v-model="previewTemplate.languageCode" />
                    </b-form-group> -->

                    <hr>

                    <b-form-group label="Include Inactive Templates">
                        <b-check switch v-model="previewTemplate.all" />
                    </b-form-group>

                    <b-form-group label="Use Specific Template">
                        <b-select v-model="previewTemplate.templateId" :options="templateOptions" />
                    </b-form-group>

                    <b-button block variant="primary" @click="getPreview">Preview Label</b-button>
                </b-col>
                <b-col cols="12" xl="8">
                    <b-overlay :show="loadingPreview">
                        <template v-if="previewUrl">
                            <img v-show="!previewImageError" :src="previewUrl" @load="previewImageError = false"
                                @error="previewImageError = true" />
                            <h5 v-if="previewImageError">Failed to load preview</h5>
                            <hr>
                            <strong>Template:</strong>
                            {{ previewContent?.template?.name }}
                        </template>
                        <template v-if="false">
                            <hr>
                            {{ previewContent }}
                        </template>
                    </b-overlay>
                </b-col>
            </b-row>
        </b-modal>
    </card>
</template>

<script lang="ts">
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { Store } from '@/models/Store';
import Axios from 'axios';
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { Program } from '@/models/Program';
import { ValidValues } from '@/data/ValidValues';
import { toRaw } from 'vue';
import { LabelTemplate, LabelTemplateType } from '@/models/LabelTemplate';
import EnumSelector from '@/components/EnumSelector.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import { Drug } from '@/models/Drug/Drug';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

const baseTokensBottleLabels = [
    "BrandingName",
    "BrandingPhone",
    "StoreAddress1",
    "StoreAddress2",
    "StoreCityStateZip",
    "StoreID",
    "RxNumber",
    "RfNumber",
    "RfNumberDisplay",
    "PrescriberDisplayName",
    "PatientLastName",
    "PatientFirstName",
    "PatientMiddleName",
    "FillDate",
    "Dispensed",
    "UnitOfMeasure",
    "DrugName",
    "DrugNameLong",
    "Directions",
    "GenericFor",
    "GenericForLong",
    "DrugExpirationDate",
    "RefillsRemaining",
    "ExpirationDate",
    "LotNumber",
    "Marketer",
    "PharmacistName",
    "TechInitials",
    "ChildCaps",
    "Warning1",
    "Warning2",
    "Warning3",
    "Warning4",
    "Warning5",
    "LabelCustomization",
    "WrittenDate",
    "PatientAddress1",
    "PatientAddress2",
    "PatientAddressCity",
    "PatientAddressState",
    "PatientAddressZip",
    "NDC10",
    "NDC11",
    "ScalableDirections",
    "ScalableWarning1",
    "ScalableWarning2",
    "ScalableWarning3",
    "ScalableWarning4",
    "ScalableWarning5"
];

const tokensByType = {
    0: [...baseTokensBottleLabels],
    1: [...baseTokensBottleLabels, "Url"]
}

@Component({
    name: "LabelTemplatesPage",
    components: {
        BottomPagerBar,
        Multiselect,
        EnumSelector,
        SearchComponent
    }
})
export default class LabelTemplatesPage extends Vue {

    protected stores: Array<Store> = [];
    protected programs: Array<Program> = [];
    protected states = ValidValues.addressStates;

    protected LabelTemplateType = LabelTemplateType;

    protected showTemplateTester = false;

    @Ref("templateZpl") protected templateZpl: any;

    private templates: LabelTemplate[] = [];
    get tableFields() {
        return [
            { key: "id" },
            { key: "active" },
            { key: "name" },
            { key: "type" },
            { key: "stores" },
            { key: "programs" },
            { key: "states" },
            { key: "actions", label: "" }
        ]
    }

    private activeTemplate: LabelTemplate | null = null;
    private previewTemplate = {
        type: LabelTemplateType.bottleLabel,
        store: new Store(),
        program: new Program(),
        state: this.states[0],
        drug: new Drug(),
        languageCode: "en-US",
        all: false,
        templateId: null
    };

    get availableTokens() {
        if (!this.activeTemplate) return [];
        return tokensByType[this.activeTemplate.type];
    }

    async mounted() {
        try {
            const stores = await Axios.get<Store[]>(`/Store/`);
            this.stores = stores.data.map(s => new Store(s));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

        try {
            const programs = await Axios.get<Program[]>(`/Program/`);
            this.programs = programs.data.map(s => new Program(s));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

        this.previewTemplate.store = this.stores[0];
        this.previewTemplate.program = this.programs[0];

        this.getTemplates();
        this.getInitialDrugForPreview();
    }

    async getTemplates() {
        try {
            const resp = await this.$http.get<LabelTemplate[]>("/LabelTemplate");
            this.templates = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getInitialDrugForPreview() {
        try {
            const resp = await this.$http.get<Drug[]>("/Drug/?allSources=false&searchTerm=test");
            this.previewTemplate.drug = Object.assign(new Drug(), resp.data[0]);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async loadTemplate(item: any) {
        this.activeTemplate = structuredClone(toRaw(item));
    }

    newTemplate() {
        this.activeTemplate = {
            id: 0,
            active: true,
            name: "",
            type: LabelTemplateType.bottleLabel,
            template: ""
        };
    }

    async saveTemplate(e: any) {
        e?.preventDefault();

        try {
            await this.$http.post("/LabelTemplate", this.activeTemplate);
            this.activeTemplate = null;
            this.getTemplates();
            this.$notification(NotificationOptions.successSaveNotificationPreset("Label Template"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    private previewContent: any = null;
    private loadingPreview = false;
    private previewImageError = false;

    async getPreview() {

        try {
            this.loadingPreview = true;
            const resp = await this.$http.get("/LabelTemplate/preview", {
                params: {
                    type: this.previewTemplate.type,
                    storeId: this.previewTemplate.store?.id,
                    programId: this.previewTemplate.program?.id,
                    state: this.previewTemplate.state,
                    packageId: this.previewTemplate.drug?.packageID,
                    languageCode: this.previewTemplate.languageCode,
                    includeInactive: this.previewTemplate.all,
                    templateId: this.previewTemplate.templateId
                }
            });
            this.previewContent = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loadingPreview = false;
        }
    }

    get previewUrl() {
        if (!this.previewContent?.content) return "";
        return `http://api.labelary.com/v1/printers/12dpmm/labels/4x2/0/${encodeURIComponent(this.previewContent.content)}`;
    }

    async getDefaultTemplate() {
        if (!this.activeTemplate) return;

        try {
            const resp = await this.$http.get<string>("/LabelTemplate/default", {
                params: {
                    type: this.activeTemplate.type
                }
            })
            this.activeTemplate.template = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get templateOptions() {
        const filteredTemplates = this.templates.filter(t => t.type === this.previewTemplate.type)
            .map(t => ({ value: t.id, text: t.name }));
        return [{ value: null, text: "" }, ...filteredTemplates];
    }

    @Watch("previewTemplate.type")
    previewTemplateTypeChange() {
        this.previewTemplate.templateId = null;
    }

    addToken(tkn: string) {
        const currentValue = this.activeTemplate!.template;
        const textArea = this.templateZpl.$el;
        const cursorStartPosition = textArea?.selectionStart ?? currentValue.length;
        const cursorEndPosition = textArea?.selectionEnd ?? currentValue.length;
        const newValue = `${currentValue.slice(0, cursorStartPosition)}{{${tkn}}}${currentValue.slice(cursorEndPosition)}`;
        this.activeTemplate!.template = newValue;

        this.$nextTick(() => {
            setTimeout(() => {
                const newPosition = cursorStartPosition + tkn.length + 4;
                textArea.selectionStart = newPosition;
                textArea.selectionEnd = newPosition;
                textArea.focus();
            }, 100)
        });
    }
}

</script>

<style scoped>
::v-deep(textarea) {
    height: calc(100vh - 305px) !important;
    max-height: calc(100vh - 305px) !important;
}
</style>
